import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@clerk/react';

import { useOnboardingStore } from '@/store/onboardingStore';

/**
 * Wraps /app routes. Authenticated users who haven't finished onboarding
 * are redirected to /onboarding. Completed users pass through.
 *
 * `completedUsers` (persisted in localStorage) is the authoritative source of
 * truth for whether a user has finished onboarding. When a different user logs
 * in, session-scoped state (business info, voice answers) is reset for privacy,
 * but `completedUsers` is preserved so returning users are not re-onboarded.
 */
export function OnboardingGuard() {
  const { isLoaded, userId } = useAuth();
  const storedUserId = useOnboardingStore((s) => s.userId);
  const completedUsers = useOnboardingStore((s) => s.completedUsers);
  const reset = useOnboardingStore((s) => s.reset);

  // Reset session-scoped data when a different user logs in (privacy/data isolation).
  useEffect(() => {
    if (userId && storedUserId !== null && storedUserId !== userId) {
      reset();
    }
  }, [userId, storedUserId, reset]);

  // Wait for Clerk to fully resolve the user identity before making routing decisions.
  if (!isLoaded || !userId) return null;

  // `completedUsers` persists across resets, so it remains the correct check
  // even after a user-switch reset clears `currentStep` back to 'onboarding'.
  if (completedUsers.includes(userId)) {
    return <Outlet />;
  }

  return <Navigate to="/onboarding" replace />;
}
